import { useAsObservableSource, useLocalStore } from 'mobx-react-lite';
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useTranslation } from '@gem/modules/i18n';

import Tasks from 'APP/Tasks';
import { RouterLink, RouterState } from 'APP/router/constants';
import { buildPath } from 'APP/router/helpers/buildPath';
import useGetState from 'APP/router/hooks/useGetState';
import { useNavigateTo } from 'APP/router/hooks/useNavigateTo';
import Entities from 'STORE';

export const useArticlePreviewPresenter = ({ groupId }) => {
  const { t } = useTranslation();
  const navigateTo = useNavigateTo();
  const params = useParams();
  const navigate = useNavigate();
  const hasGoBack = useGetState(RouterState.hasGoBack);

  const source = useAsObservableSource({ params, navigateTo });

  const presenter = useLocalStore(() => ({
    goBack: () => {
      if (hasGoBack) {
        navigate(-1);
        return;
      }

      navigate(buildPath(RouterLink.Channel, { id: groupId }));
    },

    get group() {
      return Entities.GroupsStore.getGroupById(groupId);
    },

    get message() {
      if (!presenter.group) {
        return null;
      }

      return presenter.group.messagesStore.getMessageById(source.params.messageId) || null;
    },

    get isShowFooter() {
      return presenter.message && !presenter.message.isScheduled;
    },

    async delete() {
      const canDelete = await Tasks.app.showConfirm({ text: t('delete_message') });

      if (!canDelete) {
        return false;
      }

      const res = await presenter.message.delete();
      if (res) {
        presenter.goBack();
      }
    },

    openMedia: (mediaId) => {
      Tasks.group.openMediaGallery({
        groupId,
        mediaId,
        messageId: presenter.message.id,
        messageIds: [presenter.message.id],
        showMessageInfo: false,
      });
    },

    updateViewsAndCommentsCount() {
      clearTimeout(presenter.timer);
      if (Entities.App.isFocused && presenter.isShowFooter) {
        Tasks.messaging.updateMessages([
          {
            messageId: presenter.message?.id,
            groupId,
          },
        ]);
      }
      presenter.timer = setTimeout(presenter.updateViewsAndCommentsCount, 5000);
    },

    async loadMessage() {
      let message = null;
      if (presenter.group) {
        const loadedMessages = await Tasks.messaging.loadMessagesByIds(presenter.group.id, [
          source.params.messageId,
        ]);

        message = loadedMessages[0] || null;
      }

      if (!message) {
        Tasks.app.showAlert(t('message_has_been_deleted'));
        presenter.goBack();
      }
    },

    init() {
      if (!presenter.message) {
        presenter.loadMessage();
        return;
      }

      if (!presenter.isShowFooter) {
        return;
      }

      presenter.updateViewsAndCommentsCount();
      Tasks.messaging.increaseMessagesView({
        groupId: Entities.GroupsStore.activeGroupId,
        messageIds: [presenter.message?.id],
      });

      const threadId = presenter.message?.threadId;
      if (threadId && !Entities.GroupsStore.getGroupById(threadId)) {
        Tasks.thread.getThreadInfoById(threadId);
      }
    },
  }));

  useEffect(() => {
    presenter.init();
  }, [presenter.message?.id]);

  useEffect(() => {
    presenter.updateViewsAndCommentsCount();
  }, [Entities.App.isFocused]);

  useEffect(() => {
    return () => {
      clearInterval(presenter.timer);
    };
  }, []);

  return presenter;
};
